<template>
  <tab-page-template>
    <v-container fluid>
      <data-profile-info
          :data-profile-view="selfDataProfile"
          :loading="selfDataProfileLoading">
        <template #pull-request-parameter>
          <!-- PR パラメータ発行側 -->
          <template v-if="tradeContract.amiIssuer">
            <v-col cols="3">
              <v-switch
                  :label="tradeContract.sendType === 'pull' ? 'PULL リクエスト可' : 'PULL リクエスト不可'"
                  v-model="tradeContract.sendType"
                  true-value="pull"
                  false-value="push"
                  :readonly="!isEditablePullRequestSwitch"
                  hide-details>
              </v-switch>
            </v-col>
            <v-col cols="3">
              <text-field-like
                  v-if="tradeContract.sendType === 'pull' && tradeContract.isSelfProduce"
                  label="PULL リクエストパラメータの指定方法"
                  :value="parameterDescription"
                  :loading="parameterDescriptionLoading"
                  placeholder="未設定"
                  hide-details
                  multi-line>
              </text-field-like>
            </v-col>
          </template>

          <!-- PR パラメータ受領側 -->
          <template v-else>
            <v-col cols="3">
              <text-field-like
                  label="PUSH/PULL"
                  :value="tradeContract.sendType === 'pull' ? 'PULL リクエスト可' : 'PULL リクエスト不可'"
                  hide-details>
              </text-field-like>
            </v-col>
            <v-col cols="3">
              <text-field-like
                  v-if="tradeContract.sendType === 'pull'"
                  label="PULL リクエストパラメータの指定方法"
                  :value="parameterDescription"
                  :loading="parameterDescriptionLoading"
                  placeholder="未設定"
                  hide-details
                  multi-line>
              </text-field-like>
            </v-col>
          </template>
        </template>
      </data-profile-info>
    </v-container>

    <template #tab-items>
      <v-tab>データ項目プロファイル</v-tab>
      <v-tab-item>
        <v-skeleton-loader
            type="table-thead, table-tbody"
            max-height="200"
            v-if="selfDataPropertyProfilesLoading">
        </v-skeleton-loader>
        <data-property-profile-list
            :data-property-profiles="selfDataPropertyProfiles"
            v-else>
        </data-property-profile-list>
      </v-tab-item>
    </template>

    <template #after-tabs>
      <app-header :errors="errors">
        <error-panel :errors="errors" small></error-panel>
        <v-spacer></v-spacer>
        <v-btn
            elevation="0"
            color="primary"
            style="align-self: center;"
            @click="$emit('next-step')">
          {{ nextPageName }}に進む
        </v-btn>
      </app-header>
    </template>
  </tab-page-template>
</template>

<script>
import DataPropertyProfileSource from "@/components/organisms/DataPropertyProfileSource";
import DataProfileInfo from "@/components/organisms/DataProfileInfo.vue";
import TabPageTemplate from "@/components/templates/TabPageTemplate.vue";
import DataPropertyProfileList from "@/components/organisms/DataPropertyProfileList.vue";
import DataProfileSource from "@/components/organisms/DataProfileSource";
import TradeContractView, {emptyTradeContractView} from "@/components/pages/contract/TradeContract/TradeContractView";
import TextFieldLike from "@/components/molecules/TextFieldLike.vue";
import ErrorPanel from "@/components/molecules/ErrorPanel.vue";
import AppHeader from "@/components/atoms/AppHeader.vue";
import {PropType} from "vue";

export default {
  name: "DataProfile",
  components: {
    AppHeader,
    ErrorPanel, TextFieldLike, DataPropertyProfileList, TabPageTemplate, DataProfileInfo
  },
  mixins: [DataProfileSource, DataPropertyProfileSource],
  props: {
    tradeContracts: {
      type: Array,
      required: true
    },
    tradeContractIndex: {
      type: Number,
      default: 1
    },
    editable: {
      type: Boolean
    },
    steps: {
      type: Array,
      required: true,
    },
    step: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      selfDataProfile: {},
      selfDataProfileLoading: true,
      selfDataPropertyProfiles: [],
      selfDataPropertyProfilesLoading: true,
      otherDataProfile: {},
      otherDataProfileLoading: true,
    }
  },
  computed: {
    tradeContract() {
      return this.tradeContracts[this.tradeContractIndex - 1] ?? emptyTradeContractView();
    },
    parameterDescription() {
      if(this.tradeContract.isSelfProduce) {
        return this.selfDataProfile?.dataImplementation?.parameterDescription;
      } else {
        return this.otherDataProfile?.dataImplementation?.parameterDescription;
      }
    },
    parameterDescriptionLoading() {
      if(this.tradeContract.isSelfProduce) {
        return this.selfDataProfileLoading;
      } else {
        return this.otherDataProfileLoading;
      }
    },
    isEditablePullRequestSwitch() {
      return this.editable && !this.tradeContract.isSubscribeMode && !this.tradeContract.isPublishMode;
    },
    errors() {
      const errors = [];
      if(this.isEditablePullRequestSwitch) {
        errors.push({
          level: 'good',
          message: `PULL リクエスト可・不可の選択後、${this.nextPageName}に進むことができます。`
        });
      } else {
        errors.push({
          level: 'good',
          message: `確認後、${this.nextPageName}に進むことができます。`
        });
      }
      return errors;
    },
    nextPageName() {
      return this.steps[this.step + 1]?.label ?? 'サービスの選択';
    },
  },
  watch: {
    'tradeContract.selfDataProfileId': {
      immediate: true,
      async handler(selfDataProfileId) {
        if(selfDataProfileId) {
          this.selfDataProfileLoading = true;
          this.selfDataPropertyProfilesLoading = true;

          try {
            this.selfDataProfile = await this.loadDataProfile(selfDataProfileId);
          } finally {
            this.selfDataProfileLoading = false;
          }

          try {
            this.selfDataPropertyProfiles = (await this.loadDataPropertyProfiles(selfDataProfileId))
                .map(dpp => {
                  const dpi = dpp.dataPropertyImplementation;
                  // const commonDpd = dpp.commonDataPropertyDefinition;
                  const localDpd = dpp.localDataPropertyDefinition;
                  return {
                    ...dpp,
                    index: dpi.index,
                    defaultValue: dpi.defaultValue,
                    name: localDpd?.name,
                    description: localDpd?.description,
                    dataType: localDpd?.dataType ?? 'string',
                    isPrimaryKey: localDpd?.isPrimaryKey ?? false,
                    isRequired: localDpd?.isRequired ?? false,
                  };
                });
          } finally {
            this.selfDataPropertyProfilesLoading = false;
          }
        } else {
          this.selfDataProfile = {};
          this.selfDataPropertyProfiles = [];
        }
      }
    },
    'tradeContract.otherDataProfileId': {
      immediate: true,
      async handler(otherDataProfileId) {
        if(otherDataProfileId && this.tradeContract.isOtherProduce) {
          this.otherDataProfileLoading = true;

          // otherDataProfile の生成
          try {
            this.otherDataProfile = await this.loadAssetsDataProfile(otherDataProfileId);
          } finally {
            this.otherDataProfileLoading = false;
          }
        } else {
          this.otherDataProfile = {};
        }
      }
    },
  }
}
</script>

<style scoped>

</style>
